import React, {useEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import PropTypes from 'prop-types';
import ModalWrapper from '../Modal/Modal';
import {loginStyles} from '../Modal/Modal.config';
import {
  FEEDBACK_RESULT,
  ORDER_FEEDBACK,
  ORDER_FEEDBACK_COPY,
} from '../../constants/constants';
import {
  Sheet,
  Header,
  CloseButton,
  Title,
  Subtitle,
  List,
  Notice,
  Card,
  ProductRow,
  Thumb,
  ItemCode,
  ItemName,
  RatingRow,
  Stars,
  StarButton,
  CommentButton,
  CommentBadge,
  CommentBox,
  ItemError,
  Footer,
  RatedCount,
  LaterButton,
  SubmitButton,
  ThankYou,
} from './FeedbackModal.style';
import {
  CloseIcon,
  CommentIcon,
  StarIcon,
  TickIcon,
} from './FeedbackModal.icons';

const STARS = Array.from({length: ORDER_FEEDBACK.TOTAL_STARS}, (_, i) => i + 1);

// How far the on-screen keyboard covers the page, and the height left above
// it. Mobile browsers shrink only the visual viewport when the keyboard
// opens, so the sheet, fixed to the bottom on a phone, would sit behind it.
// Only while a comment box has focus, so pinch-zooming doesn't move the sheet.
const NO_KEYBOARD = {inset: 0, height: null};
const getKeyboard = isCommentFocused => {
  const viewport = typeof window !== 'undefined' && window.visualViewport;
  if (!viewport || !isCommentFocused) {
    return NO_KEYBOARD;
  }
  const inset = Math.round(
    window.innerHeight - viewport.height - viewport.offsetTop
  );
  return inset > 0 ? {inset, height: Math.round(viewport.height)} : NO_KEYBOARD;
};

// Room kept between a comment box scrolled into view and the list's edge.
const COMMENT_SCROLL_MARGIN = 16;

// iOS Safari ignores overflow: hidden on the body, so a swipe outside the list
// would scroll the page behind the sheet and keep scrolling it until the next
// tap in the list. Fixing the body in place stops that; its scroll position is
// put back afterwards (as SearchPage does for its filters).
const lockBodyScroll = () => {
  const {style} = document.body;
  const scrollY = window.scrollY;
  const saved = {
    overflow: style.overflow,
    position: style.position,
    top: style.top,
    left: style.left,
    right: style.right,
  };
  style.overflow = 'hidden';
  style.position = 'fixed';
  style.top = `-${scrollY}px`;
  style.left = '0';
  style.right = '0';
  return () => {
    Object.assign(style, saved);
    window.scrollTo(0, scrollY);
  };
};

// Scrolls the list, and only the list (scrollIntoView can pan the whole page
// on iOS), so the box is in view. A box taller than the list shows its top.
const scrollIntoList = (list, box) => {
  const listRect = list.getBoundingClientRect();
  const boxRect = box.getBoundingClientRect();
  const above = listRect.top - (boxRect.top - COMMENT_SCROLL_MARGIN);
  const below = boxRect.bottom + COMMENT_SCROLL_MARGIN - listRect.bottom;
  if (above > 0) {
    list.scrollTop -= above;
  } else if (below > 0) {
    list.scrollTop += Math.min(below, -above);
  }
};

const allOpen = items =>
  items.reduce((open, item) => ({...open, [item.itemId]: true}), {});

/**
 * Star rating + optional comment per product, shared by the post-delivery
 * order popup (OrderFeedbackModal) and the product page's "Leave a review".
 * Holds only the form being filled in; what's sent and how it went come from
 * the caller. onClose/onSecondary get the number of items rated, for
 * analytics.
 */
const FeedbackModal = ({
  items,
  title,
  subtitle,
  secondaryLabel,
  onSecondary,
  onClose,
  onSubmit,
  submitting,
  result,
  itemErrors,
  alreadyReviewedItemIds,
  commentsOpenByDefault,
  showRatedCount,
}) => {
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [openComments, setOpenComments] = useState(() =>
    commentsOpenByDefault ? allOpen(items) : {}
  );
  const [keyboard, setKeyboard] = useState(NO_KEYBOARD);
  const commentRefs = useRef({});
  const listRef = useRef(null);

  const getFocusedComment = () =>
    Object.values(commentRefs.current).find(
      box => box === document.activeElement
    );

  // Keeps the comment being typed in visible: scrolled into view in the list,
  // and above the keyboard once it's open.
  const showFocusedComment = () => {
    const box = getFocusedComment();
    setKeyboard(getKeyboard(!!box));
    if (box) {
      // After the sheet has taken its new size.
      window.requestAnimationFrame(() => {
        if (listRef.current) {
          scrollIntoList(listRef.current, box);
        }
      });
    }
  };

  useEffect(lockBodyScroll, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return undefined;
    }
    viewport.addEventListener('resize', showFocusedComment);
    viewport.addEventListener('scroll', showFocusedComment);
    return () => {
      viewport.removeEventListener('resize', showFocusedComment);
      viewport.removeEventListener('scroll', showFocusedComment);
    };
  }, []);

  const isAlreadyReviewed = itemId =>
    (alreadyReviewedItemIds || []).indexOf(itemId) >= 0;
  const alreadyReviewedKey = (alreadyReviewedItemIds || []).join(',');

  // An item the server says was already reviewed loses its rating and
  // comment, so it can't go out with the next submit. Keyed on the joined ids
  // (a string) rather than the array, which is a new object every update.
  useEffect(() => {
    const ids = alreadyReviewedKey ? alreadyReviewedKey.split(',') : [];
    if (!ids.length) {
      return;
    }
    const without = values =>
      Object.keys(values)
        .filter(itemId => ids.indexOf(itemId) < 0)
        .reduce((kept, itemId) => ({...kept, [itemId]: values[itemId]}), {});
    setRatings(without);
    setComments(without);
    setOpenComments(without);
  }, [alreadyReviewedKey]);

  const rateableItems = items.filter(item => !isAlreadyReviewed(item.itemId));
  const ratedItems = rateableItems.filter(item => ratings[item.itemId]);
  // The "please submit again" notice only makes sense while something failed
  // that can be sent again; an already-reviewed item can't.
  const hasRetryableError = rateableItems.some(
    item => itemErrors && item.itemId in itemErrors
  );

  const setRating = (itemId, rating) =>
    setRatings(prev => ({...prev, [itemId]: rating}));

  const setComment = (itemId, comment) =>
    setComments(prev => ({...prev, [itemId]: comment}));

  // Opening a comment box focuses it. The box is rendered synchronously so
  // the focus happens within the tap, which mobile browsers need to open the
  // keyboard.
  const toggleComment = itemId => {
    const opening = !openComments[itemId];
    flushSync(() =>
      setOpenComments(prev => ({...prev, [itemId]: !prev[itemId]}))
    );
    if (opening && commentRefs.current[itemId]) {
      commentRefs.current[itemId].focus();
    }
  };

  const handleSubmit = () => {
    if (!ratedItems.length || submitting) {
      return;
    }
    onSubmit(
      ratedItems.map(item => ({
        itemId: item.itemId,
        rating: ratings[item.itemId],
        comment: (comments[item.itemId] || '').trim(),
      }))
    );
  };

  const handleClose = () => {
    if (!submitting) {
      onClose(ratedItems.length);
    }
  };

  const renderCloseButton = (
    <CloseButton
      type="button"
      onClick={handleClose}
      aria-label={ORDER_FEEDBACK_COPY.CLOSE}
      data-rw="feedback-modal--close"
    >
      <CloseIcon />
    </CloseButton>
  );

  const renderThankYou = (
    <>
      <Header>
        {renderCloseButton}
        <Title>{ORDER_FEEDBACK_COPY.SUCCESS_TITLE}</Title>
        <Subtitle>{ORDER_FEEDBACK_COPY.SUCCESS_TEXT}</Subtitle>
      </Header>
      <ThankYou>
        <SubmitButton
          type="button"
          onClick={handleClose}
          data-rw="feedback-modal--done"
        >
          {ORDER_FEEDBACK_COPY.DONE}
        </SubmitButton>
      </ThankYou>
    </>
  );

  const renderNotice =
    hasRetryableError &&
    (result === FEEDBACK_RESULT.PARTIAL ||
      result === FEEDBACK_RESULT.FAILED) ? (
      <Notice role="alert">
        {result === FEEDBACK_RESULT.PARTIAL && (
          <strong>{ORDER_FEEDBACK_COPY.PARTIAL_FAILURE_TITLE}. </strong>
        )}
        {result === FEEDBACK_RESULT.PARTIAL
          ? ORDER_FEEDBACK_COPY.PARTIAL_FAILURE_TEXT
          : ORDER_FEEDBACK_COPY.FAILURE_TEXT}
      </Notice>
    ) : null;

  const renderItem = item => {
    const rating = ratings[item.itemId] || 0;
    const comment = comments[item.itemId] || '';
    const isCommentOpen = !!openComments[item.itemId];
    const isLocked = isAlreadyReviewed(item.itemId);
    return (
      <Card key={item.itemId} data-rw={`feedback-modal--item-${item.itemId}`}>
        <ProductRow>
          <Thumb>
            {item.image && <img src={item.image} alt="" loading="lazy" />}
          </Thumb>
          <div>
            <ItemCode>{item.itemId}</ItemCode>
            <ItemName>{item.itemName}</ItemName>
          </div>
        </ProductRow>
        {!isLocked && (
          <RatingRow>
            <Stars role="radiogroup" aria-label={item.itemName}>
              {STARS.map(star => (
                <StarButton
                  key={star}
                  type="button"
                  role="radio"
                  aria-checked={rating === star}
                  aria-label={ORDER_FEEDBACK_COPY.STAR_LABEL(
                    star,
                    item.itemName
                  )}
                  $filled={star <= rating}
                  disabled={submitting}
                  onClick={() => setRating(item.itemId, star)}
                >
                  <StarIcon filled={star <= rating} />
                </StarButton>
              ))}
            </Stars>
            <CommentButton
              type="button"
              $open={isCommentOpen}
              aria-expanded={isCommentOpen}
              aria-label={
                isCommentOpen
                  ? ORDER_FEEDBACK_COPY.HIDE_COMMENT
                  : ORDER_FEEDBACK_COPY.ADD_COMMENT
              }
              title={ORDER_FEEDBACK_COPY.ADD_COMMENT}
              disabled={submitting}
              onClick={() => toggleComment(item.itemId)}
            >
              <CommentIcon />
              {!!comment.trim() && (
                <CommentBadge>
                  <TickIcon />
                </CommentBadge>
              )}
            </CommentButton>
          </RatingRow>
        )}
        {!isLocked && isCommentOpen && (
          <CommentBox
            ref={box => {
              if (box) {
                commentRefs.current[item.itemId] = box;
              } else {
                delete commentRefs.current[item.itemId];
              }
            }}
            value={comment}
            maxLength={ORDER_FEEDBACK.COMMENT_MAX_LENGTH}
            placeholder={ORDER_FEEDBACK_COPY.COMMENT_PLACEHOLDER}
            aria-label={ORDER_FEEDBACK_COPY.COMMENT_PLACEHOLDER}
            disabled={submitting}
            onChange={e => setComment(item.itemId, e.target.value)}
            onFocus={showFocusedComment}
          />
        )}
        {itemErrors && item.itemId in itemErrors && (
          <ItemError role="alert">
            {itemErrors[item.itemId] || ORDER_FEEDBACK_COPY.ITEM_FAILED}
          </ItemError>
        )}
      </Card>
    );
  };

  // While typing on a phone the keyboard leaves little room, so the header
  // gives its space to the list.
  const renderForm = (
    <>
      {!keyboard.inset && (
        <Header>
          {renderCloseButton}
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Header>
      )}
      <List ref={listRef}>
        {renderNotice}
        {items.map(renderItem)}
      </List>
      <Footer>
        <RatedCount aria-live="polite">
          {showRatedCount &&
            ORDER_FEEDBACK_COPY.RATED_COUNT(
              ratedItems.length,
              rateableItems.length
            )}
        </RatedCount>
        <LaterButton
          type="button"
          onClick={() => onSecondary(ratedItems.length)}
          disabled={submitting}
          data-rw="feedback-modal--secondary"
        >
          {secondaryLabel}
        </LaterButton>
        <SubmitButton
          type="button"
          onClick={handleSubmit}
          disabled={!ratedItems.length || submitting}
          data-rw="feedback-modal--submit"
        >
          {submitting
            ? ORDER_FEEDBACK_COPY.SUBMITTING
            : ORDER_FEEDBACK_COPY.SUBMIT}
        </SubmitButton>
      </Footer>
    </>
  );

  return (
    <ModalWrapper
      customStyles={loginStyles}
      isOpen={true}
      closeModal={handleClose}
    >
      <Sheet
        role="dialog"
        aria-label={title}
        data-rw="feedback-modal"
        style={
          keyboard.inset
            ? {bottom: keyboard.inset, maxHeight: keyboard.height}
            : undefined
        }
      >
        {result === FEEDBACK_RESULT.SUCCESS ? renderThankYou : renderForm}
      </Sheet>
    </ModalWrapper>
  );
};

FeedbackModal.propTypes = {
  // [{itemId, itemName, image}]
  items: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  secondaryLabel: PropTypes.string.isRequired,
  onSecondary: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // Called with [{itemId, rating, comment}] for the rated items.
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  result: PropTypes.oneOf([...Object.values(FEEDBACK_RESULT), null]),
  // {[itemId]: message}; a null message falls back to ITEM_FAILED.
  itemErrors: PropTypes.object,
  alreadyReviewedItemIds: PropTypes.array,
  commentsOpenByDefault: PropTypes.bool,
  showRatedCount: PropTypes.bool,
};

FeedbackModal.defaultProps = {
  subtitle: null,
  submitting: false,
  result: null,
  itemErrors: {},
  alreadyReviewedItemIds: [],
  commentsOpenByDefault: false,
  showRatedCount: true,
};

export default FeedbackModal;
